import { useState } from 'react';

/**
 * 场景卡片组件
 * 展示当前活跃的消费场景
 */
export default function ScenarioCards({ scenarios = [] }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);

  if (!scenarios.length) {
    return (
      <div style={{
        padding: '48px 24px',
        textAlign: 'center',
        borderRadius: 16,
        background: 'rgba(255,255,255,0.015)',
        border: '1px solid rgba(255,255,255,0.04)',
      }}>
        <p style={{ fontSize: 14, color: '#64766D' }}>正在探测场景信号...</p>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.min(scenarios.length, 4)}, 1fr)`,
      gap: 16,
    }}>
      {scenarios.map((scenario, i) => (
        <ScenarioCard
          key={scenario.id || i}
          scenario={scenario}
          isHovered={hoveredIdx === i}
          onHover={() => setHoveredIdx(i)}
          onLeave={() => setHoveredIdx(null)}
        />
      ))}
    </div>
  );
}

function ScenarioCard({ scenario, isHovered, onHover, onLeave }) {
  const growthColor = scenario.expectedGrowth >= 50 ? '#E8C85A'
    : scenario.expectedGrowth >= 35 ? '#69B89A' : '#8EB9D6';

  return (
    <div
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{
        padding: '24px 20px',
        borderRadius: 16,
        background: isHovered
          ? 'rgba(255,255,255,0.035)'
          : 'rgba(255,255,255,0.02)',
        border: `1px solid ${isHovered ? 'rgba(232,200,90,0.12)' : 'rgba(255,255,255,0.05)'}`,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      {/* 背景微光 */}
      <div style={{
        position: 'absolute', top: -30, right: -30,
        width: 80, height: 80, borderRadius: '50%',
        background: `${growthColor}08`,
        transform: isHovered ? 'scale(2.5)' : 'scale(1)',
        transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'none',
      }} />

      {/* 图标 */}
      <div style={{
        fontSize: 28,
        marginBottom: 14,
        lineHeight: 1,
      }}>
        {scenario.icon || '🎯'}
      </div>

      {/* 名称 */}
      <div style={{
        fontSize: 15, fontWeight: 600,
        color: '#F3F5EF',
        marginBottom: 6,
        letterSpacing: '-0.01em',
      }}>
        {scenario.name}
      </div>

      {/* 描述 */}
      <div style={{
        fontSize: 12, color: '#64766D',
        lineHeight: 1.5,
        marginBottom: 16,
        minHeight: 36,
      }}>
        {scenario.description?.slice(0, 40)}...
      </div>

      {/* 增长指数 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 11, color: '#64766D', marginBottom: 2 }}>需求增长</div>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 2,
            fontVariantNumeric: 'tabular-nums',
          }}>
            <span style={{
              fontSize: 22, fontWeight: 600, color: growthColor,
              letterSpacing: '-0.02em',
            }}>
              {scenario.expectedGrowth}
            </span>
            <span style={{ fontSize: 13, color: growthColor }}>%</span>
          </div>
        </div>

        {/* 置信度 */}
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#64766D', marginBottom: 2 }}>置信度</div>
          <ConfidenceBar value={scenario.confidence || 75} />
        </div>
      </div>

      {/* 品类标签 */}
      {scenario.affectedCategories && (
        <div style={{
          display: 'flex', gap: 4, flexWrap: 'wrap',
          marginTop: 14, paddingTop: 14,
          borderTop: '1px solid rgba(255,255,255,0.04)',
        }}>
          {scenario.affectedCategories.split(',').slice(0, 3).map((cat, j) => (
            <span key={j} style={{
              fontSize: 11, color: '#8BA8B8',
              padding: '2px 8px', borderRadius: 8,
              background: 'rgba(139,168,184,0.06)',
            }}>
              {cat.trim()}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfidenceBar({ value }) {
  const color = value >= 85 ? '#69B89A' : value >= 70 ? '#E8C85A' : '#D48BA0';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
      <span style={{
        fontSize: 14, fontWeight: 600, color,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}%
      </span>
      <div style={{
        width: 48, height: 3, borderRadius: 2,
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${value}%`, height: '100%',
          borderRadius: 2,
          background: `linear-gradient(90deg, ${color}80, ${color})`,
          transition: 'width 600ms ease',
        }} />
      </div>
    </div>
  );
}
